import * as _ from "lodash-es";
import axios from "axios";
import { notifyAxiosError } from "@/notify";
import { MAX_PAGE_SIZE } from "@/constants";
import { isEnabled } from "@/features/metafields/services";

export default {
  namespaced: true,
  state: {
    customCategoriesMap: {},
    isCustomCategoriesFetching: false,
    isCustomCategoriesFetched: false,
    customCategoriesOptionsMap: {},
  },
  mutations: {
    setCustomCategories: function (state, customCategories) {
      state.customCategoriesMap = _.keyBy(customCategories, "id");
    },
    setCustomCategoriesOptions: function (state, customCategoriesOptions) {
      state.customCategoriesOptionsMap = _.keyBy(customCategoriesOptions, "id");
    },
    setCustomCategoriesFetching: function (state, isCustomCategoriesFetching) {
      state.isCustomCategoriesFetching = isCustomCategoriesFetching;
    },
    setCustomCategoriesFetched: function (state, isCustomCategoriesFetched) {
      state.isCustomCategoriesFetched = isCustomCategoriesFetched;
    },
    updateCustomCategory: function (state, customCategory) {
      state.customCategoriesMap = {
        ...state.customCategoriesMap,
        [customCategory.id]: customCategory,
      };
    },
  },
  getters: {
    customCategoriesList(state) {
      return _.sortBy(Object.values(state.customCategoriesMap), "name");
    },
    enabledCustomCategories(state, getters) {
      return getters.customCategoriesList.filter((c) => c.is_enabled);
    },
    /** Get categories which are enabled in specified form. */
    getEnabledCustomCategoriesForForm(state, getters) {
      return (metaFieldsFormCode) =>
        getters.enabledCustomCategories.filter((category) =>
          isEnabled(category.field_settings.enabled, metaFieldsFormCode),
        );
    },
    customCategoriesOptionsList(state) {
      return _.sortBy(Object.values(state.customCategoriesOptionsMap), "name");
    },
    /** Return comma separated list of option names for category. */
    getOptionNamesByCategory(state) {
      return (optionIds, categoryId) =>
        optionIds
          .map((optionId) => state.customCategoriesOptionsMap[optionId])
          .filter((option) => option && option.category === categoryId)
          .map((option) => option.name)
          .join(", ");
    },
    /**
     * Get custom categories column configs for a table.
     * Each category should be in separate column.
     * @returns Array
     */
    customCategoriesFields(state, getters) {
      return getters.enabledCustomCategories.map((c) => ({
        key: c.id,
        label: c.name,
        /**
         * @param {undefined} value `item[key]`
         * @param {String} key Id of category
         * @param {Object} item Student profile
         * @returns String
         */
        formatter: (value, key, item) =>
          getters.getOptionNamesByCategory(item.custom_categories_options, key),
        sortable: true,
      }));
    },
    /**
     * Get custom categories with is_weight_score_enabled=ture for review table.
     * Each category should be in separate column.
     * @returns Array
     */
    customCategoriesWithWeightFields(state, getters) {
      return getters.enabledCustomCategories
        .filter((c) => c.is_weight_score_enabled)
        .map((c) => ({
          key: c.id,
          label: c.name,
          formatter: (value, key, item) =>
            getters.getOptionNamesByCategory(item.custom_categories_options, key),
        }));
    },
    /** Custom category options for select/checkbox/radio. */
    optionsForSelectByCategory(state, getters) {
      return _.groupBy(
        getters.customCategoriesOptionsList.map((option) => ({
          category: option.category,
          value: option.id,
          text: option.name,
        })),
        "category",
      );
    },
    getOptionsForSelectByCategory(state, getters) {
      return (categoryId) => {
        return getters.optionsForSelectByCategory[categoryId] || [];
      };
    },
    /** Concatenate options of category, that has been changed, with options of other categories. */
    handleOptionsChanged(state) {
      return (oldOptions, newOptions, categoryId) => [
        ...newOptions,
        ...oldOptions.filter(
          (id) => state.customCategoriesOptionsMap[id].category !== categoryId,
        ),
      ];
    },
  },
  actions: {
    fetchCustomCategories(context) {
      if (
        context.state.isCustomCategoriesFetching ||
        context.state.isCustomCategoriesFetched
      ) {
        return;
      }
      context.commit("setCustomCategoriesFetching", true);
      const requests = [
        axios.get("/profile-categories/", { params: { page_size: MAX_PAGE_SIZE } }),
        axios.get("/profile-category-options/", { params: { page_size: MAX_PAGE_SIZE } }),
      ];
      return Promise.all(requests).then(
        ([categoriesResponse, optionsResponse]) => {
          context.commit("setCustomCategoriesFetching", false);
          context.commit("setCustomCategoriesFetched", true);
          context.commit("setCustomCategoriesOptions", optionsResponse.data.results);
          context.commit("setCustomCategories", categoriesResponse.data.results);
        },
        (error) => {
          context.commit("setCustomCategoriesFetching", false);
          notifyAxiosError(error);
        },
      );
    },
  },
};
